import React from 'react';

import { getClients, getRoleById } from 'common-src/models/Client';

import useCustomSelector from 'src/hooks/useCustomSelector';

import styles from './ReassignModal.module.scss';

const getClientName = (client) => {
  if (!client) return '';
  if (typeof client.getName === 'function') return client.getName(false, true);

  return `${client.firstName || ''} ${client.lastName || ''}`.trim();
};

const ReassignModal = ({ task, onClose, onConfirm, isSubmitting = false }) => {
  const clients = useCustomSelector((state) => getClients(state)) || [];
  const [query, setQuery] = React.useState('');
  const [selectedClientId, setSelectedClientId] = React.useState(null);
  const currentOwner = `${task?.owner || ''}`.trim().toLowerCase();

  const clientsWithLabel = React.useMemo(
    () =>
      clients
        .map((client) => ({
          client,
          id: client?.id,
          label: getClientName(client),
          role: client?.role || (client?.roleId ? getRoleById(client.roleId) : ''),
        }))
        .filter((item) => item.id && item.label)
        .sort((a, b) => a.label.localeCompare(b.label)),
    [clients],
  );

  const visibleClients = React.useMemo(() => {
    const text = query.trim().toLowerCase();
    if (!text) return clientsWithLabel;

    return clientsWithLabel.filter(({ label, role }) => {
      const haystack = `${label} ${role}`.toLowerCase();
      return haystack.includes(text);
    });
  }, [clientsWithLabel, query]);

  const selected = clientsWithLabel.find((item) => item.id === selectedClientId);
  const isCurrentOwnerSelected = selected?.label?.toLowerCase() === currentOwner;

  return (
    <div
      className={styles.modalOverlay}
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className={styles.modalCard}
        role="dialog"
        aria-modal="true"
        aria-labelledby="reassign-title"
      >
        <div className={styles.modalHeader}>
          <div>
            <div id="reassign-title" className={styles.modalTitle}>
              Reassign Task
            </div>
            <div className={styles.modalSubtitle}>Select a user from clients list.</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className={styles.modalCloseButton}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className={styles.modalBody}>
          <div className={styles.modalTaskLabel}>{task?.title}</div>
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className={styles.searchInput}
            placeholder="Search users..."
          />
          <div className={styles.list}>
            {!visibleClients.length && <div className={styles.emptyState}>No users found</div>}
            {visibleClients.map(({ client, id, label, role }) => {
              const isSelected = selectedClientId === id;
              const isCurrentOwner = label.toLowerCase() === currentOwner;
              return (
                <button
                  key={id}
                  type="button"
                  className={styles.listItem}
                  onClick={() => !isCurrentOwner && setSelectedClientId(id)}
                  disabled={isCurrentOwner}
                >
                  <span className={styles.listItemInfo}>
                    <span className={styles.listItemName}>{label}</span>
                    <span className={styles.listItemRole}>{role || '—'}</span>
                  </span>
                  {isCurrentOwner && <span className={styles.currentOwnerTag}>Current</span>}
                  {isSelected && !isCurrentOwner && <span className={styles.selectedMark}>✓</span>}
                </button>
              );
            })}
          </div>
        </div>

        <div className={styles.modalFooter}>
          <button type="button" onClick={onClose} className={styles.modalSecondaryAction}>
            Cancel
          </button>
          <button
            type="button"
            onClick={async () => {
              if (!selected || isCurrentOwnerSelected) return;
              const shouldClose = await onConfirm?.(selected.client);
              if (shouldClose !== false) onClose();
            }}
            className={styles.modalPrimaryAction}
            disabled={!selected || isCurrentOwnerSelected || isSubmitting}
          >
            {isSubmitting ? 'Reassigning...' : 'Reassign'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReassignModal;
